// react
import { ReactNode, useState } from 'react';

// types
import { Order, Pagination } from '../../../../../@types';

// hooks
import { useFetch } from '../../../../../hooks/useFetch';

// mui
import { SelectChangeEvent } from '@mui/material';

// utils
import { defaultPagination } from '../../../../../utils/defaults';

export interface StatsTabRenderProps<T> {
	data: T[];
	isLoading: boolean;
	onSort: (args: { order: Order; value: string }) => void;
	TableFooterProps: {
		hasSelectRows: boolean;
		onChangePage: (event: unknown, page: number) => void;
		onChangeRowsPerPage: (event: SelectChangeEvent<unknown>, child: ReactNode) => void;
		pagination: Pagination;
	};
}

export interface StatsTabProps<T> {
	/** stats endpoint type: 'counts' | 'activity' | 'evidence' */
	type: string;
	/** only fetches while the tab is active */
	isActive: boolean;
	/** default order-by column (must be backend-whitelisted: idWbg | name | onboardingDate) */
	defaultOrderBy?: string;
	render: (props: StatsTabRenderProps<T>) => ReactNode;
}

/**
 * Data-fetching wrapper for a single stats tab. Handles sorting and
 * pagination against /stats/{type} and lazily fetches only while active.
 */
export const StatsTab = <T,>({
	type,
	isActive,
	defaultOrderBy = 'name',
	render,
}: StatsTabProps<T>) => {
	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// state
	const [data, setData] = useState<T[]>([]);
	const [order, setOrder] = useState<Order>('DESC');
	const [orderBy, setOrderBy] = useState(defaultOrderBy);
	const [pagination, setPagination] = useState<Pagination>(defaultPagination);

	// fetch
	const queryParams = new URLSearchParams({
		limit: String(pagination.perPage),
		order,
		orderBy,
		page: String(pagination.page),
	});

	const { isLoading } = useFetch({
		isEnabled: isActive,
		url: `${process.env.REACT_APP_API_URL}/stats/${type}?${queryParams.toString()}`,
		options: {
			headers: {
				Authorization: `Bearer ${accessToken}`,
				User: String(idToken),
			},
		},
		onSuccess: (res) => {
			if (res.data) {
				setData(res.data as T[]);
			}
			if (res.pagination) {
				setPagination(res.pagination);
			}
		},
	});

	// handlers
	const handleSort = ({ order: newOrder, value }: { order: Order; value: string }) => {
		setOrder(newOrder);
		setOrderBy(value);
		setPagination(defaultPagination);
	};

	const handleChangePage = (_: unknown, newPage: number) => {
		setPagination((prev) => ({ ...prev, page: newPage }));
	};

	const handleChangeRowsPerPage = (e: SelectChangeEvent<unknown>, _child: ReactNode) => {
		setPagination({ ...defaultPagination, perPage: Number(e.target.value) });
	};

	return (
		<>
			{render({
				data,
				isLoading,
				onSort: handleSort,
				TableFooterProps: {
					hasSelectRows: true,
					onChangePage: handleChangePage,
					onChangeRowsPerPage: handleChangeRowsPerPage,
					pagination,
				},
			})}
		</>
	);
};
